import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import isEqual from 'lodash-es/isEqual';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import moment from 'moment';

import { checkAndRoun } from '../../../../helpers/topicsHelpers';
import capitalize from '../../../../helpers/capitalize';
import usePrevious from '../../../../hooks/usePrevious';
import { UKDisableMessage } from '../../../../data/ukPrivateCompanyInsights';

import ConditionalTooltip from '../../CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const SingleInvestorMobileList = (props) => {
  const {
    currentPage, pageSize, data: propsData, totalValue, hasInsightsAccess, hasInsightsTrialAccess,
    sortOrder, sortField, onSort,
  } = props;
  const [data, setData] = useState(propsData);
  const disableMessage = UKDisableMessage(true);

  const prevPropsData = usePrevious(propsData);
  useEffect(() => {
    if (!isEqual(propsData, prevPropsData)) {
      setData(propsData);
    }
  }, [propsData]);

  return (
    <div className={cx('investors', { no_access: !hasInsightsAccess })}>
      <div className={Styles['investors-header']}>
        <div className={Styles['investors-row']}>
          <div className={cx('investors-cell', 'rowNumber')}>#</div>
          <div className={cx('investors-cell', 'companyName')}>Investment</div>
          <div className={cx('investors-cell', 'stakeholderName')}>Investor Name in Filings</div>
          <div className={cx('investors-cell', 'classOfShare')}>Class of Shares</div>
          <div className={cx('investors-cell', 'amountOfShares')}>Number of Shares</div>
          <div className={cx('investors-cell', 'CS01filingDate')}>
            <div className={Styles['table-sorting']} onClick={() => onSort('CS01filingDate')}>
              <div>
                Date Reported
                <br />
                <span className="font-weight-normal">(number of shares)</span>
              </div>
              <div className={cx('sort-arrows')}>
                {(sortField !== 'CS01filingDate' || sortOrder === 'asc') && (
                  <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                )}
                {(sortField !== 'CS01filingDate' || sortOrder === 'desc') && (
                  <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                )}
              </div>
            </div>
          </div>
          <div className={cx('investors-cell', 'currency')}>Currency</div>
          <div className={cx('investors-cell', 'valueOfHoldings')}>
            <div className={Styles['table-sorting']} onClick={() => onSort('valueOfHoldings')}>
              <div>
                Value of Holdings
              </div>
              <ConditionalTooltip
                tooltipProps={{
                  hasBigScreenDesign: true,
                  isCentered: true,
                  dynamicPosition: true,
                  contentStyle: { left: 0 },
                  className: Styles['sort-arrows-tooltip'],
                }}
                tooltipMessage={disableMessage}
                showTooltip={hasInsightsTrialAccess}
              >
                <div className={cx('sort-arrows')}>
                  {(sortField !== 'valueOfHoldings' || sortOrder === 'asc') && (
                    <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                  )}
                  {(sortField !== 'valueOfHoldings' || sortOrder === 'desc') && (
                    <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                  )}
                </div>
              </ConditionalTooltip>
            </div>
          </div>
          <div className={cx('investors-cell', 'expandIcon')} />
        </div>
        <div
          className={cx('investors-row', 'total_row')}
        >
          <div className={cx('investors-cell', 'rowNumber')} />
          <div className={cx('investors-cell', 'companyName')}>
            TOTAL
          </div>
          <div className={cx('investors-cell', 'stakeholderName')} />
          <div className={cx('investors-cell', 'classOfShare')} />
          <div className={cx('investors-cell', 'amountOfShares')} />
          <div className={cx('investors-cell', 'CS01filingDate')} />
          <div className={cx('investors-cell', 'currency')} />
          <div className={cx('investors-cell', 'pricePerShare')} />
          <div className={cx('investors-cell', 'valueOfHoldings')}>
            {checkAndRoun(totalValue.valueOfHoldings, 2, true)}
          </div>
          <div className={cx('investors-cell', 'expandIcon')} />
        </div>
      </div>
      <div className={Styles['investors-body']}>
        {data
          .map((obj, i) => (
            <DataList
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              currentData={obj}
              currentRow={((currentPage - 1) * pageSize) + (i + 1)}
            />
          ))}
      </div>
    </div>
  );
};

const DataList = ({ currentData, currentRow }) => {
  const [isExpand, setIsExpand] = useState(false);

  return (
    <div className={cx('investors-row-holder')}>
      <div className={Styles['investors-row']}>
        <div className={cx('investors-cell', 'rowNumber')}>
          {currentRow}
        </div>
        <div className={cx('investors-cell', 'companyName')}>
          {currentData.url ? (
            <Link to={currentData.url}>
              {currentData.companyName || '-'}
            </Link>
          ) : (
            <>
              {currentData.companyName || '-'}
            </>
          )}
        </div>
        <div className={cx('investors-cell', 'stakeholderName')}>
          {currentData.stakeholderName}
        </div>
        <div className={cx('investors-cell', 'classOfShare')}>
          {currentData.classOfShares}
        </div>
        <div className={cx('investors-cell', 'amountOfShares')}>
          {typeof currentData.amountOfShares === 'number' ? checkAndRoun(currentData.amountOfShares || 0, 2, true) : '-'}
          <Link
            className={Styles.files_links}
            to={`/directory/filings/GB/companieshouse-gb/${currentData.CS01filingId}`}
          >
            <FontAwesomeIcon icon={faFileAlt} />
          </Link>
        </div>
        <div className={cx('investors-cell', 'CS01filingDate')}>
          {currentData.CS01filingDate ? moment(currentData.CS01filingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-'}
        </div>
        <div className={cx('investors-cell', 'currency')}>
          {currentData.currency || '-'}
        </div>
        <div className={cx('investors-cell', 'valueOfHoldings')}>
          {checkAndRoun(currentData.valueOfHoldings, 2, true) || '-'}
        </div>
        <div
          className={cx('investors-cell', 'expandIcon')}
          onClick={() => setIsExpand(!isExpand)}
        >
          <FontAwesomeIcon icon={isExpand ? faChevronUp : faChevronDown} />
        </div>
      </div>
      {isExpand && (
        <div className={cx('investors-extra')}>
          <div className={cx('investors-extra-cell', 'stakeholderName')}>
            <div className={cx('investors-extra-title')}>
              Investor Name in Filings
            </div>
            <div className={cx('investors-extra-value')}>
              {currentData.stakeholderName}
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'classOfShare')}>
            <div className={cx('investors-extra-title')}>
              Class of Shares
            </div>
            <div className={cx('investors-extra-value')}>
              {capitalize(currentData.classOfShares || '-')}
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'CS01filingDate')}>
            <div className={cx('investors-extra-title')}>
              Date Reported
              <br />
              (number of shares)
            </div>
            <div className={cx('investors-extra-value')}>
              {currentData.CS01filingDate ? moment(currentData.CS01filingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-'}
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'SH01filingDate')}>
            <div className={cx('investors-extra-title')}>
              Date Reported
              <br />
              (price per share)
            </div>
            <div className={cx('investors-extra-value')}>
              {currentData.SH01filingDate ? moment(currentData.SH01filingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-'}
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'currency')}>
            <div className={cx('investors-extra-title')}>
              Currency
            </div>
            <div className={cx('investors-extra-value')}>
              {currentData.currency || '-'}
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'amountOfShares')}>
            <div className={cx('investors-extra-title')}>
              Number of Shares
            </div>
            <div className={cx('investors-extra-value')}>
              {typeof currentData.amountOfShares === 'number' ? checkAndRoun(currentData.amountOfShares || 0, 2, true) : '-'}
              <Link
                className={Styles.files_links}
                to={`/directory/filings/GB/companieshouse-gb/${currentData.CS01filingId}`}
              >
                <FontAwesomeIcon icon={faFileAlt} />
              </Link>
            </div>
          </div>
          <div className={cx('investors-extra-cell', 'pricePerShare')}>
            <div className={cx('investors-extra-title')}>
              Last Round Share Price
            </div>
            <div className={cx('investors-extra-value')}>
              {typeof currentData.pricePerShare === 'number' ? checkAndRoun(currentData.pricePerShare || 0, 2, true, true, true) : '-'}
              <Link
                className={Styles.files_links}
                to={`/directory/filings/GB/companieshouse-gb/${currentData.SH01filingId}`}
              >
                <FontAwesomeIcon icon={faFileAlt} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleInvestorMobileList;
